import {
  activateRepositoryWritesOrganizationWideAfterReviewAsUser,
  type DashboardPushBrokerAllowlistItem,
  type DashboardPushBrokerAllowlistReviewResponse,
  type DashboardPushBrokerEnabledPolicy,
  type DashboardPushBrokerOverviewResponse,
  type DashboardPushBrokerPolicy,
  enableOrDisablePushForARepositoryAttachmentAsUser,
  getPushBrokerOverviewAsUser,
  listPushPoliciesForAChannelEnvironmentAsUser,
  reAuthorizeRevokedRepositoryWritesAsUser,
  reviewRepositoryWritesBeforeAnOrgWideActivationAsUser,
  revokeRepositoryWritesOrganizationWideAsUser,
} from "../dashboard-client";

// ---------------------------------------------------------------------------
// Push broker — operator reads/writes through the dashboard's
// org-scoped mirror of the orchestrator's global-principal routes. The
// dashboard server enforces org ownership and stamps the session identity;
// these wrappers are deliberately thin. The push-plane installation is the
// existing App A installation recorded in the provider row metadata — no
// registration surface exists.
// ---------------------------------------------------------------------------

export type PushBrokerOverview = DashboardPushBrokerOverviewResponse;
export type PushBrokerAllowlistItem = DashboardPushBrokerAllowlistItem;
export type PushBrokerPolicy = DashboardPushBrokerPolicy;
export type PushBrokerAllowlistReview =
  DashboardPushBrokerAllowlistReviewResponse;
export type PushBrokerEnabledPolicy = DashboardPushBrokerEnabledPolicy;

/** Test seam: production passes nothing (module-level client); tests inject
 *  baseUrl + fetch so the real generated request path is exercised. */
export interface PushBrokerTransport {
  baseUrl?: string;
  fetch?: typeof globalThis.fetch;
}

export async function fetchPushBrokerOverview(
  providerId?: string,
  transport: PushBrokerTransport = {},
): Promise<PushBrokerOverview> {
  const { data } = await getPushBrokerOverviewAsUser({
    ...transport,
    query: providerId ? { providerId } : {},
  });
  return data;
}

export async function fetchPushBrokerPolicies(
  channelEnvironmentId: string,
  transport: PushBrokerTransport = {},
): Promise<PushBrokerPolicy[]> {
  const { data } = await listPushPoliciesForAChannelEnvironmentAsUser({
    ...transport,
    query: { channelEnvironmentId },
  });
  return data.policies;
}

export interface SetPushPolicyInput {
  channelEnvironmentId: string;
  providerId: string;
  repositoryId: string;
  pushEnabled: boolean;
}

export async function setPushPolicy(
  input: SetPushPolicyInput,
  transport: PushBrokerTransport = {},
): Promise<Pick<PushBrokerPolicy, "revision" | "pushEnabled">> {
  const { data } = await enableOrDisablePushForARepositoryAttachmentAsUser({
    ...transport,
    body: {
      channelEnvironmentId: input.channelEnvironmentId,
      providerId: input.providerId,
      pushEnabled: input.pushEnabled,
      repositoryId: input.repositoryId,
    },
  });
  return data.policy;
}

// ---------------------------------------------------------------------------
// Repository write authorization (org allowlist) — org-admin repair surface
// (R2.4). Distinct from the per-attachment push toggle: these change what
// EVERY workspace in the organization may push to.
// ---------------------------------------------------------------------------

export interface PushBrokerRepositoryKey {
  providerId: string;
  repositoryId: string;
}

export async function fetchPushBrokerAllowlistReview(
  key: PushBrokerRepositoryKey,
  transport: PushBrokerTransport = {},
): Promise<PushBrokerAllowlistReview> {
  const { data } = await reviewRepositoryWritesBeforeAnOrgWideActivationAsUser({
    ...transport,
    query: key,
  });
  return data;
}

export async function activateRepositoryWritesAfterReview(
  key: PushBrokerRepositoryKey,
  transport: PushBrokerTransport = {},
): Promise<PushBrokerAllowlistItem> {
  const { data } =
    await activateRepositoryWritesOrganizationWideAfterReviewAsUser({
      ...transport,
      body: key,
    });
  return data.repository;
}

export async function revokeRepositoryWrites(
  key: PushBrokerRepositoryKey,
  transport: PushBrokerTransport = {},
): Promise<PushBrokerAllowlistItem> {
  const { data } = await revokeRepositoryWritesOrganizationWideAsUser({
    ...transport,
    body: key,
  });
  return data.repository;
}

export async function reauthorizeRepositoryWrites(
  key: PushBrokerRepositoryKey,
  transport: PushBrokerTransport = {},
): Promise<PushBrokerAllowlistItem> {
  const { data } = await reAuthorizeRevokedRepositoryWritesAsUser({
    ...transport,
    body: key,
  });
  return data.repository;
}
